import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  bffConfigService,
  currencySettingsService,
  type Currency,
  type CurrencySetting,
} from '@resort-os/api-client';

interface CurrencyContextType {
  currency: Currency | null;
  currencies: CurrencySetting[];
  isLoading: boolean;
  refetch: () => void;
}

const CurrencyContext = createContext<CurrencyContextType>({
  currency: null,
  currencies: [],
  isLoading: true,
  refetch: () => {},
});

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrency] = useState<Currency | null>(null);
  const [currencies, setCurrencies] = useState<CurrencySetting[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchCurrency = async () => {
    setIsLoading(true);
    try {
      const [defaultRes, listRes] = await Promise.allSettled([
        currencySettingsService.getDefault(),
        currencySettingsService.list(),
      ]);

      if (defaultRes.status === 'fulfilled') {
        const d = defaultRes.value;
        setCurrency({ code: d.code, name: d.name, symbol: d.symbol });
      } else {
        try {
          const data = await bffConfigService.getDefaultCurrency();
          setCurrency(data);
        } catch {
          setCurrency({ code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh' });
        }
      }

      if (listRes.status === 'fulfilled') {
        setCurrencies((listRes.value.currencies || []).filter(c => c.is_active));
      }
    } catch (error) {
      console.error('Failed to fetch currency settings:', error);
      setCurrency({ code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrency();
  }, []);

  return (
    <CurrencyContext.Provider value={{ currency, currencies, isLoading, refetch: fetchCurrency }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}

export function useFormatCurrency() {
  const { currency } = useCurrency();

  return (amount: number | string): string => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return 'KES 0';

    if (!currency) {
      return `KES ${num.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }

    return `${currency.symbol} ${num.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
}
